import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import { AlertTriangle, Copy, RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { logger } from "@/lib/logger"

import { MSAL_LOGIN_START_KEY, RETURN_TO_KEY } from "../utils/auth-constants"
import { getAuthRetryTarget } from "../utils/auth-error-page-utils"
import { clearAuthError, readAuthError, storeAuthError } from "../utils/auth-error-store"
import { clearAuthRedirectAttempts } from "../utils/auth-loop-guard"

export function AuthErrorPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const storedError = useMemo(() => readAuthError(), [])

  const reasonFromQuery = searchParams.get("reason")
  const messageFromQuery = searchParams.get("message")

  const details = useMemo(() => {
    if (reasonFromQuery) {
      return storeAuthError({
        reason: reasonFromQuery,
        message: messageFromQuery ?? undefined,
        provider: storedError?.provider ?? "unknown",
        returnTo: storedError?.returnTo ?? sessionStorage.getItem(RETURN_TO_KEY) ?? undefined,
        context: storedError?.context,
      })
    }
    return storedError
  }, [messageFromQuery, reasonFromQuery, storedError])

  const diagnosticsText = useMemo(() => {
    if (!details) return t("authError.noDiagnostics", "No stored auth error details.")
    return JSON.stringify(details, null, 2)
  }, [details, t])

  const handleCopyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(diagnosticsText)
      toast.success(t("authError.diagnosticsCopied", "Diagnostics copied to clipboard"))
    } catch (error) {
      logger.warn("Failed to copy diagnostics to clipboard", error as Error)
      toast.error(t("authError.diagnosticsCopyFailed", "Failed to copy diagnostics"))
    }
  }

  const handleRetry = () => {
    window.location.href = getAuthRetryTarget(window.location.origin)
  }

  const handleClearAndRetry = () => {
    clearAuthRedirectAttempts()
    clearAuthError()
    sessionStorage.removeItem(RETURN_TO_KEY)
    sessionStorage.removeItem(MSAL_LOGIN_START_KEY)
    window.location.href = getAuthRetryTarget(window.location.origin)
  }

  const handleSignIn = () => {
    // Simply navigate to the app root. EasyAuth will enforce login, or
    // the app's auth flow will handle MSAL login if needed.
    clearAuthRedirectAttempts()
    clearAuthError()
    window.location.href = getAuthRetryTarget(window.location.origin)
  }

  return (
    <section className="bg-background flex h-screen w-screen items-center justify-center p-4">
      <Card className="border-destructive/50 w-full max-w-xl">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <AlertTriangle className="text-destructive h-12 w-12" />
          </div>
          <CardTitle className="text-destructive text-2xl">
            {t("authError.title", "We couldn’t sign you in")}
          </CardTitle>
          <CardDescription className="pt-2">
            {t(
              "authError.description",
              "This usually happens when a session expires or authentication is misconfigured."
            )}
          </CardDescription>
        </CardHeader>

        <CardContent className="text-muted-foreground space-y-4 text-sm">
          <div className="space-y-1">
            <p>
              <b>{t("authError.reasonLabel", "Reason:")}</b>{" "}
              {details?.reason ?? t("authError.unknownReason", "Unknown")}
            </p>
            {details?.message && (
              <p className="break-words">
                <b>{t("authError.messageLabel", "Message:")}</b> {details.message}
              </p>
            )}
            {details?.id && (
              <p>
                <b>{t("authError.errorReferenceLabel", "Error reference:")}</b>{" "}
                <code className="font-mono">{details.id}</code>
              </p>
            )}
          </div>

          <details className="border-border/50 bg-muted/20 rounded-md border p-3">
            <summary className="text-foreground/80 cursor-pointer select-none">
              {t("authError.diagnosticsSummary", "Diagnostics (for support)")}
            </summary>
            <pre className="mt-3 max-h-64 overflow-auto text-xs break-words whitespace-pre-wrap">
              {diagnosticsText}
            </pre>
          </details>
        </CardContent>

        <CardFooter className="flex flex-col justify-center gap-2 sm:flex-row">
          <Button className="w-full sm:w-auto" variant="destructive" onClick={handleSignIn}>
            {t("authError.signInAgain", "Sign in again")}
          </Button>
          <Button className="w-full sm:w-auto" variant="secondary" onClick={handleRetry}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t("authError.retry", "Retry")}
          </Button>
          <Button className="w-full sm:w-auto" variant="outline" onClick={handleClearAndRetry}>
            {t("authError.clearAndRetry", "Clear and retry")}
          </Button>
          <Button
            className="w-full sm:w-auto"
            variant="outline"
            onClick={() => void handleCopyDiagnostics()}
          >
            <Copy className="mr-2 h-4 w-4" />
            {t("authError.copyDiagnostics", "Copy diagnostics")}
          </Button>
        </CardFooter>
      </Card>
    </section>
  )
}

AuthErrorPage.displayName = "AuthErrorPage"
